/**
 * Greenhouse/Ashby-class structured screening portal (Meridian Talent demo).
 * Role rubric → scorecard → interview plan / pipeline tray.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Link,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { adviseDemoAssistantApi, getRecruitingCatalogApi } from '../../api/demoAssistants.api';
import ClawDecisionPanel from '../../components/demo/ClawDecisionPanel';
import DemoDictateTextField from '../../components/demo/DemoDictateTextField';
import { useSpeechSynthesis } from '../../hooks/useSpeechSynthesis';

const FOREST = '#14532d';
const INK = '#0f1a14';

const BUCKET_TONE = {
  pass: { bg: '#ecfdf5', border: '#6ee7b7', fg: '#065f46', label: 'Advance' },
  hold: { bg: '#fffbeb', border: '#fcd34d', fg: '#92400e', label: 'Hold' },
  reject: { bg: '#fef2f2', border: '#fca5a5', fg: '#991b1b', label: 'Decline' },
};

function ScoreRing({ score, bucket }) {
  const tone = BUCKET_TONE[bucket] || BUCKET_TONE.hold;
  return (
    <Box
      sx={{
        width: 88,
        height: 88,
        borderRadius: '50%',
        border: `3px solid ${tone.border}`,
        bgcolor: '#fff',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.55rem', lineHeight: 1, color: tone.fg }}>
          {score}
        </Typography>
        <Typography variant="caption" color="text.secondary">/ 100</Typography>
      </Box>
    </Box>
  );
}

function PipelineTray({ queue, liveRow }) {
  const rows = useMemo(() => {
    const seed = Array.isArray(queue) ? queue : [];
    if (!liveRow) return seed;
    const already = seed.some((r) => r.id === liveRow.id);
    return already ? seed : [liveRow, ...seed];
  }, [queue, liveRow]);

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(20,83,45,0.18)',
        bgcolor: '#fff',
        overflow: 'hidden',
      }}
      data-testid="hiring-pipeline-tray"
    >
      <Box sx={{
        px: 2,
        py: 1.5,
        background: 'linear-gradient(105deg, rgba(20,83,45,0.12) 0%, #fff 70%)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <GroupsOutlinedIcon sx={{ color: FOREST }} />
          <Box>
            <Typography fontWeight={700} sx={{ color: INK }}>Pipeline queue</Typography>
            <Typography variant="caption" color="text.secondary">
              Active screens — recommended next actions
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Stack divider={<Divider />}>
        {rows.map((row) => {
          const tone = BUCKET_TONE[row.bucket] || BUCKET_TONE.hold;
          return (
            <Box key={row.id} sx={{ px: 2, py: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box>
                  <Typography fontWeight={600}>{row.candidateName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.role} · {row.stage || 'Screening'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: FOREST }}>
                    {row.recommendation}
                  </Typography>
                </Box>
                <Stack alignItems="flex-end" spacing={0.5}>
                  <Chip
                    size="small"
                    label={`${row.bucket} · ${row.score}`}
                    sx={{ bgcolor: tone.bg, color: tone.fg, textTransform: 'capitalize', fontWeight: 600 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {row.ageHours != null ? `${row.ageHours}h in stage` : 'just screened'}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          );
        })}
        {rows.length === 0 && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography color="text.secondary" variant="body2">Queue is empty.</Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

function OutcomeHero({ result }) {
  const screening = result?.screening || {};
  const scorecard = result?.scorecard || {};
  const bucket = screening.bucket || 'hold';
  const tone = BUCKET_TONE[bucket] || BUCKET_TONE.hold;
  const dims = scorecard.dimensions || [];
  const flags = screening.flags || [];
  const questions = result?.interviewQuestions || [];
  const plan = scorecard.interviewPlan || [];
  const { speak, cancel, isSpeaking, hasSupport } = useSpeechSynthesis();
  const speakText = [
    result?.routing?.primaryTrackLabel || tone.label,
    result?.candidate?.name,
    scorecard.roleTitle,
    result?.routing?.rationale,
    questions.slice(0, 2).join('. '),
  ].filter(Boolean).join('. ');

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${tone.border}`,
        bgcolor: tone.bg,
        p: { xs: 2, sm: 2.75 },
      }}
      data-testid="hiring-outcome-card"
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <ScoreRing score={screening.score} bucket={bucket} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }} alignItems="center">
            <Chip
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
              label={result?.routing?.primaryTrackLabel || tone.label}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.75)', color: tone.fg, fontWeight: 600 }}
            />
            <Chip
              label={`${screening.confidence || '—'}% confidence`}
              size="small"
              variant="outlined"
              sx={{ borderColor: tone.border, color: tone.fg }}
            />
            {scorecard.level ? (
              <Chip label={scorecard.level} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.7)' }} />
            ) : null}
            {hasSupport && (
              <Button
                size="small"
                startIcon={<VolumeUpIcon />}
                onClick={() => (isSpeaking ? cancel() : speak(speakText))}
                sx={{ textTransform: 'none', color: tone.fg }}
              >
                {isSpeaking ? 'Stop' : 'Listen'}
              </Button>
            )}
          </Stack>
          <Typography
            sx={{
              fontFamily: '"Fraunces", "Source Serif 4", Georgia, serif',
              fontWeight: 650,
              fontSize: { xs: '1.35rem', sm: '1.55rem' },
              color: INK,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            {result?.candidate?.name || 'Candidate'} · {scorecard.roleTitle}
          </Typography>
          <Typography sx={{ color: 'rgba(15,26,20,0.78)', maxWidth: 560 }}>
            {result?.routing?.rationale}
          </Typography>
        </Box>
      </Stack>

      {flags.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {flags.map((f) => (
            <Chip
              key={f.id}
              size="small"
              label={f.label}
              sx={{
                bgcolor: f.severity === 'high' ? '#fee2e2' : '#ffedd5',
                maxWidth: '100%',
                height: 'auto',
                '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
              }}
            />
          ))}
        </Stack>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1, color: INK, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <FactCheckOutlinedIcon sx={{ fontSize: 18, color: FOREST }} />
        Weighted scorecard
      </Typography>
      <Stack spacing={1.25} sx={{ mb: 2.5 }}>
        {dims.map((d) => (
          <Box key={d.id} sx={{ bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2, p: 1.25 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
              <Typography fontWeight={600}>{d.dimension}</Typography>
              <Typography variant="body2" fontWeight={700} color={tone.fg}>
                {d.scoreLabel}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  · {d.weight}%
                </Typography>
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(d.score / 5) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                mb: 0.75,
                bgcolor: 'rgba(0,0,0,0.06)',
                '& .MuiLinearProgress-bar': { bgcolor: FOREST, borderRadius: 3 },
              }}
            />
            <Typography variant="body2" color="text.secondary">{d.evidence}</Typography>
          </Box>
        ))}
      </Stack>

      {questions.length > 0 && bucket !== 'reject' && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: INK, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <QuestionAnswerOutlinedIcon sx={{ fontSize: 18, color: FOREST }} />
            Suggested interview questions
          </Typography>
          <Stack component="ol" sx={{ m: 0, pl: 2.25 }} spacing={0.5}>
            {questions.map((q) => (
              <Typography component="li" key={q} variant="body2" color="text.secondary">{q}</Typography>
            ))}
          </Stack>
        </Box>
      )}

      {plan.length > 0 && bucket === 'pass' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Interview plan
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {plan.map((step, i) => (
              <Chip
                key={step}
                size="small"
                label={`${i + 1}. ${step}`}
                sx={{ bgcolor: i === 1 ? FOREST : 'rgba(255,255,255,0.85)', color: i === 1 ? '#fff' : INK }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
        <Button
          variant="contained"
          sx={{
            textTransform: 'none',
            bgcolor: FOREST,
            borderRadius: 2,
            px: 2.5,
            py: 1.1,
            fontWeight: 600,
            '&:hover': { bgcolor: '#166534' },
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          {bucket === 'pass' ? 'Advance & notify panel' : bucket === 'hold' ? 'Request more signal' : 'Send decline'}
        </Button>
        <Button
          variant="outlined"
          sx={{ textTransform: 'none', borderRadius: 2, borderColor: FOREST, color: FOREST }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Screen another candidate
        </Button>
      </Stack>

      {Array.isArray(result?.routing?.nextSteps) && (
        <Box sx={{ mt: 2.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Next steps
          </Typography>
          <Stack component="ol" sx={{ m: 0, pl: 2.25 }} spacing={0.35}>
            {result.routing.nextSteps.map((step) => (
              <Typography component="li" key={step} variant="body2" color="text.secondary">{step}</Typography>
            ))}
          </Stack>
        </Box>
      )}

      <ClawDecisionPanel
        claw={result?.claw}
        handoffPacket={result?.handoffPacket}
        accent={FOREST}
        defaultOpen
      />
    </Box>
  );
}

export default function HiringPortalPage() {
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [mode, setMode] = useState('screen'); // screen | pipeline
  const [step, setStep] = useState(0);
  const [roleId, setRoleId] = useState('');
  const [candidateId, setCandidateId] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  const [sponsorship, setSponsorship] = useState('not required');
  const [location, setLocation] = useState('');
  const [experienceSummary, setExperienceSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getRecruitingCatalogApi();
        if (!cancelled) {
          setCatalog(res?.catalog || null);
          const firstRole = res?.catalog?.roles?.[0]?.id;
          if (firstRole) setRoleId(firstRole);
        }
      } catch (err) {
        if (!cancelled) setCatalogError(err?.message || err?.error || 'Could not load catalog');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const brand = catalog?.brand || { name: 'Meridian Talent', tagline: 'Structured screening', primaryColor: FOREST };
  const roles = catalog?.roles || [];
  const candidates = catalog?.candidates || [];
  const selectedRole = roles.find((r) => r.id === roleId) || null;

  const pickDemoCandidate = useCallback((cand) => {
    setCandidateId(cand.id);
    setCandidateName(cand.candidateName || '');
    setRoleId(cand.roleId || roleId);
    setYearsExperience(String(cand.yearsExperience ?? ''));
    setSponsorship(cand.sponsorship || 'not required');
    setLocation(cand.location || '');
    setExperienceSummary(cand.experienceSummary || '');
    setResult(null);
    setStep(1);
    setError(null);
  }, [roleId]);

  const submit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = {
        candidateId: candidateId || undefined,
        candidateName,
        roleId,
        roleApplied: selectedRole?.title,
        yearsExperience: Number(yearsExperience) || 0,
        sponsorship,
        location,
        experienceSummary,
      };
      const raw = await adviseDemoAssistantApi('recruiting', body);
      const payload = raw?.result ? raw : raw?.data || raw;
      const adviseResult = payload?.result || payload;
      if (!adviseResult?.screening && !adviseResult?.routing) {
        throw new Error(payload?.error || 'Unexpected advise response');
      }
      setResult(adviseResult);
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Advise failed');
    } finally {
      setLoading(false);
    }
  }, [
    candidateId, candidateName, roleId, selectedRole, yearsExperience,
    sponsorship, location, experienceSummary,
  ]);

  const livePipelineRow = useMemo(() => {
    if (!result?.screening) return null;
    return {
      id: `LIVE-${result.candidate?.name || candidateName}`,
      candidateName: result.candidate?.name || candidateName,
      role: result.scorecard?.roleTitle || selectedRole?.title,
      bucket: result.screening.bucket,
      score: result.screening.score,
      stage: 'Just screened',
      ageHours: 0,
      recommendation: result.routing?.nextSteps?.[0] || result.routing?.primaryTrackLabel,
    };
  }, [result, candidateName, selectedRole]);

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        bgcolor: '#f4f6f4',
        backgroundImage:
          'radial-gradient(ellipse 80% 50% at 8% -10%, rgba(20,83,45,0.14), transparent),'
          + 'radial-gradient(ellipse 55% 40% at 100% 0%, rgba(120,53,15,0.06), transparent)',
      }}
      data-testid="hiring-portal"
    >
      <Box
        component="header"
        sx={{
          borderBottom: '1px solid rgba(15,26,20,0.08)',
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(10px)',
          position: 'sticky',
          top: 0,
          zIndex: 4,
        }}
      >
        <Box sx={{
          maxWidth: 1080,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: 1.75,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Fraunces", "Source Serif 4", Georgia, serif',
                fontWeight: 700,
                fontSize: { xs: '1.45rem', sm: '1.75rem' },
                letterSpacing: '-0.03em',
                color: brand.primaryColor || FOREST,
                lineHeight: 1.1,
              }}
            >
              {brand.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {brand.tagline || 'Structured screening'}
              {' · '}
              <Link component={RouterLink} to="/studio/help/recruiting" underline="hover" color="inherit">
                How this works
              </Link>
              {' · '}
              <Link component={RouterLink} to="/" underline="hover" color="inherit">
                InterviewFlow
              </Link>
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small"
              variant={mode === 'screen' ? 'contained' : 'outlined'}
              startIcon={<PersonSearchOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={() => setMode('screen')}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                ...(mode === 'screen'
                  ? { bgcolor: FOREST, '&:hover': { bgcolor: '#166534' } }
                  : { color: FOREST, borderColor: FOREST }),
              }}
            >
              Screen
            </Button>
            <Button
              size="small"
              variant={mode === 'pipeline' ? 'contained' : 'outlined'}
              startIcon={<GroupsOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={() => setMode('pipeline')}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                ...(mode === 'pipeline'
                  ? { bgcolor: FOREST, '&:hover': { bgcolor: '#166534' } }
                  : { color: FOREST, borderColor: FOREST }),
              }}
            >
              Pipeline
            </Button>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 4 } }}>
        {mode === 'pipeline' ? (
          <Stack spacing={2.5}>
            <Typography
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 650,
                fontSize: '1.35rem',
                color: INK,
              }}
            >
              Recruiting operations
            </Typography>
            <PipelineTray
              queue={result?.pipelineQueue || catalog?.pipelineQueueSeed || []}
              liveRow={livePipelineRow}
            />
            <Button onClick={() => setMode('screen')} sx={{ alignSelf: 'flex-start', textTransform: 'none', color: FOREST }}>
              ← Back to screening
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              {['Pick candidate', 'Review intake', 'Scorecard'].map((label, i) => (
                <Chip
                  key={label}
                  label={`${i + 1}. ${label}`}
                  size="small"
                  onClick={() => { if (i <= step) setStep(i); }}
                  sx={{
                    fontWeight: step === i ? 700 : 500,
                    bgcolor: step === i ? FOREST : step > i ? 'rgba(20,83,45,0.12)' : 'rgba(0,0,0,0.04)',
                    color: step === i ? '#fff' : INK,
                    cursor: i <= step ? 'pointer' : 'default',
                  }}
                />
              ))}
            </Stack>

            {catalogError && (
              <Alert severity="warning">Catalog offline — you can still enter a candidate manually. ({catalogError})</Alert>
            )}
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            {step === 0 && (
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 3,
                  border: '1px solid rgba(15,26,20,0.08)',
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 650,
                    fontSize: '1.4rem',
                    color: INK,
                    mb: 0.5,
                  }}
                >
                  Screen a candidate
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2.5, maxWidth: 520 }}>
                  Rubric-weighted scorecard with evidence, risk flags, and interview questions — try the three demo packets.
                </Typography>

                {candidates.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Try a demo packet
                    </Typography>
                    <Stack spacing={1}>
                      {candidates.map((c) => {
                        const role = roles.find((r) => r.id === c.roleId);
                        return (
                          <Button
                            key={c.id}
                            onClick={() => pickDemoCandidate(c)}
                            variant="outlined"
                            sx={{
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              borderRadius: 2,
                              borderColor: 'rgba(20,83,45,0.28)',
                              color: INK,
                              py: 1.25,
                              textAlign: 'left',
                            }}
                          >
                            <Box>
                              <Typography fontWeight={600}>
                                {c.candidateName}
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: FOREST }}>
                                  expects {c.expectedBucket}
                                </Typography>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {role?.title || c.roleId} · {c.yearsExperience} yrs · {c.location}
                              </Typography>
                            </Box>
                          </Button>
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                <FormControl size="small" fullWidth sx={{ mb: 2, maxWidth: 420 }}>
                  <InputLabel>Open role</InputLabel>
                  <Select
                    label="Open role"
                    value={roleId}
                    onChange={(e) => setRoleId(e.target.value)}
                  >
                    {roles.map((r) => (
                      <MenuItem key={r.id} value={r.id}>{r.title} · {r.team}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={() => setStep(1)}
                  sx={{
                    textTransform: 'none',
                    bgcolor: FOREST,
                    borderRadius: 2,
                    px: 2.5,
                    py: 1,
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#166534' },
                  }}
                >
                  Enter candidate manually
                </Button>
              </Box>
            )}

            {step === 1 && (
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 3,
                  border: '1px solid rgba(15,26,20,0.08)',
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 650,
                    fontSize: '1.35rem',
                    color: INK,
                    mb: 0.5,
                  }}
                >
                  Screening intake
                </Typography>
                {selectedRole && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Rubric for <strong>{selectedRole.title}</strong>
                    {selectedRole.mustHaves?.length
                      ? ` — must-haves: ${selectedRole.mustHaves.join(', ')}`
                      : ''}
                  </Typography>
                )}

                <Stack spacing={1.75} sx={{ maxWidth: 520 }}>
                  <TextField
                    label="Candidate name"
                    size="small"
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    fullWidth
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select label="Role" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                      {roles.map((r) => (
                        <MenuItem key={r.id} value={r.id}>{r.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label="Years experience"
                      size="small"
                      type="number"
                      value={yearsExperience}
                      onChange={(e) => setYearsExperience(e.target.value)}
                      fullWidth
                    />
                    <FormControl size="small" fullWidth>
                      <InputLabel>Sponsorship</InputLabel>
                      <Select
                        label="Sponsorship"
                        value={sponsorship}
                        onChange={(e) => setSponsorship(e.target.value)}
                      >
                        <MenuItem value="not required">Not required</MenuItem>
                        <MenuItem value="required">Required</MenuItem>
                        <MenuItem value="unknown">Unknown</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <TextField
                    label="Location"
                    size="small"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    fullWidth
                  />
                  <DemoDictateTextField
                    label="Experience / screening notes"
                    size="small"
                    value={experienceSummary}
                    onChange={(e) => setExperienceSummary(e.target.value)}
                    fullWidth
                    multiline
                    minRows={4}
                    helperText="Tap the mic to dictate notes"
                  />
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                  <Button onClick={() => setStep(0)} sx={{ textTransform: 'none', color: FOREST }}>Back</Button>
                  <Button
                    variant="contained"
                    disabled={loading || !candidateName.trim() || !experienceSummary.trim()}
                    onClick={submit}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FactCheckOutlinedIcon />}
                    sx={{
                      textTransform: 'none',
                      bgcolor: FOREST,
                      borderRadius: 2,
                      px: 2.5,
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#166534' },
                    }}
                    data-testid="hiring-run-screen"
                  >
                    Run scorecard
                  </Button>
                </Stack>
              </Box>
            )}

            {step === 2 && result && <OutcomeHero result={result} />}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
